import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Link } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import ButtonComponent from 'components/buttons/button';
import IntlMessageComponent from 'components/formatters/intlMessage';
import PageTitleViewComponent from 'components/pageTitle';

import { clearUserSignInError } from 'store/actions/authActions';
import { LOST_PASSWORD, RESET_2FA, SIGN_UP } from 'constants/pageRoutes';

type SignInFormComponentProps = {
  identifier: string;
  password: string;
  qrpin: string;
  smscode: string;
  twoFactorRequired: boolean;
  currentStep: string;
  isSendingSms: boolean;
  showLoginFailedMessage: boolean;
  showServerErrorMessage: boolean;
  handleLoginChange: (login: string) => void;
  handlePasswordChange: (password: string) => void;
  handleQrpinChange: (qrpin: string) => void;
  handleSmscodeChange: (smscode: string) => void;
  onLogin: () => void;
  onValidateTwoFactor: () => void;
};

const LOGIN_LOCK_KEY_PREFIX = 'loginLockUntilMs:'; // per username

const normalizeIdentifier = (v) => (v || '').toString().trim().toLowerCase();
const keyForIdentifier = (identifier) => `${LOGIN_LOCK_KEY_PREFIX}${normalizeIdentifier(identifier)}`;

export default function SignInFormComponent(props: SignInFormComponentProps) {
  const dispatch = useDispatch();

  const {
    loginFailedMessage,
    loginFailedDescription,
    showLoginFailedMessage,
    showServerErrorMessage
  } = useSelector(state => state.auth);

  const [lockUntilMs, setLockUntilMs] = useState(null);

  const [nowMs, setNowMs] = useState(Date.now());

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      props.onLogin();
    }
  };

  const onKeyDownValidate = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      props.onValidateTwoFactor();
    }
  };

  const formatRemaining = (totalSeconds) => {
    const total = Math.max(0, Number(totalSeconds || 0));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const readStoredLockUntil = (identifier) => {
    const k = keyForIdentifier(identifier);
    const raw = localStorage.getItem(k);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) ? value : 0;
  };

  const writeStoredLockUntil = (identifier, newUntilMs) => {
    const id = normalizeIdentifier(identifier);
    if (!id) return;
    if (!newUntilMs || !Number.isFinite(newUntilMs)) return;

    const k = keyForIdentifier(id);

    const existing = readStoredLockUntil(id);
    const finalUntil = Math.max(existing, newUntilMs);

    localStorage.setItem(k, String(finalUntil));
    setNowMs(Date.now());    
    setLockUntilMs(finalUntil);
  };

  const clearStoredLockUntil = (identifier) => {
    const id = normalizeIdentifier(identifier);
    if (!id) return;

    localStorage.removeItem(keyForIdentifier(id));
    setLockUntilMs(null);
  };

  const computeRemainingSeconds = (untilMs, now) => {
    if (!untilMs || !Number.isFinite(untilMs)) return 0;
    const remainingMs = untilMs - now;
    return remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
  };

  const lockRemainingSeconds = useMemo(
    () => computeRemainingSeconds(lockUntilMs, nowMs),
    [lockUntilMs, nowMs]
  );

  const isLocked = lockRemainingSeconds > 0;

  // 1) When identifier changes, load that username's lock from storage
  useEffect(() => {
    const id = normalizeIdentifier(props.identifier);
    if (!id) {
      setLockUntilMs(null);
      return;
    }

    const storedUntil = readStoredLockUntil(id);
    const now = Date.now();

    if (storedUntil > now) {
      setNowMs(now);      
      setLockUntilMs(storedUntil);
    } else {
      if (storedUntil) localStorage.removeItem(keyForIdentifier(id));
      setLockUntilMs(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.identifier]);

  // 2) If another tab updates the same username lock, keep this tab in sync
  useEffect(() => {
    const handler = (e) => {
      const id = normalizeIdentifier(props.identifier);
      if (!id) return;

      const k = keyForIdentifier(id);
      if (e.key !== k) return;

      const storedUntil = readStoredLockUntil(id);
      const now = Date.now();
      if (storedUntil > now) {
        setNowMs(now);  
        setLockUntilMs(storedUntil);
      } else {
        setLockUntilMs(null);
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.identifier]);

// 3) If server returns login_locked, store lock for CURRENT username
useEffect(() => {
  if (!showLoginFailedMessage || loginFailedMessage !== 'login_locked') return;

  const id = normalizeIdentifier(props.identifier);
  if (!id) return;

  const now = Date.now();

  // If we already have a lock in storage, do NOT rewrite it from server "minutes"
  const existingUntil = readStoredLockUntil(id);
  if (existingUntil > now) {
    setNowMs(now);
    setLockUntilMs(existingUntil);
    return;
  }

  const desc = (loginFailedDescription || '').toString();

  const match = desc.match(/(\d+)/);
  const minutes = match ? parseInt(match[1], 10) : 1;

  const seconds = Math.max(1, minutes) * 60;
  const until = now + seconds * 1000;

  writeStoredLockUntil(id, until);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showLoginFailedMessage, loginFailedMessage, loginFailedDescription, props.identifier]);

  // 4) Ticking: update "now" every second only while locked 
  useEffect(() => {
    if (!lockUntilMs) return;

    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lockUntilMs]);

  // 5) When lock hits 0: clear storage + hide message (for that username)
  useEffect(() => {
    if (!lockUntilMs) return;

    if (lockRemainingSeconds <= 0) {
      clearStoredLockUntil(props.identifier);
      dispatch(clearUserSignInError());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockRemainingSeconds, lockUntilMs, props.identifier, dispatch]);

  // Show error area if redux says so OR we have an active lock for this username
  const shouldShowError = showLoginFailedMessage || isLocked;

  let errorMessageId = '';
  let errorValues = undefined;

  if (shouldShowError) {
    // If currently locked (from storage), show lock message ALWAYS for this username
    if (isLocked) {
      errorMessageId = 'appModule.signInFailed.loginLocked';
      errorValues = { time: formatRemaining(lockRemainingSeconds) };
    } else if (loginFailedMessage === 'registration_not_complete') {
      errorMessageId = 'appModule.signInFailed.registrationNotCompleted';
    } else if (loginFailedMessage === 'locked_out') {
      errorMessageId = 'appModule.signInFailed.lockedOut';
    } else if (loginFailedMessage === 'suspended_user') {
      errorMessageId = 'appModule.signInFailed.NoActiveCompany';
    } else if (loginFailedMessage === 'login_locked') {
      // Fallback if lock wasn't stored yet for some reason
      errorMessageId = 'appModule.signInFailed.loginLocked';

      const desc = (loginFailedDescription || '').toString();
      const match = desc.match(/(\d+)/);
      const minutes = match ? parseInt(match[1], 10) : 1;
      errorValues = { time: formatRemaining(Math.max(1, minutes) * 60) };
    } else if (loginFailedMessage === 'account_blocked' || loginFailedMessage === 'blocked_user') {
      errorMessageId = 'appModule.signInFailed.accountBlocked';
    } else {
      errorMessageId = 'appModule.signInFailed';
    }
  }

  return (
    <Box mx="auto" maxWidth="350px">
      <Paper elevation={0} square>
        <Box p={4} textAlign="center">
          <PageTitleViewComponent id="page.login.title" />

          {!props.twoFactorRequired && shouldShowError && (
            <Box>
              <Typography variant="body1">
                <IntlMessageComponent id={errorMessageId} values={errorValues} />
              </Typography>

              <Link to={LOST_PASSWORD}>
                <IntlMessageComponent id="appModule.lostPassword" />
              </Link>
            </Box>
          )}

          {!props.twoFactorRequired && showServerErrorMessage && (
            <Box>
              <Typography variant="body1">
                <IntlMessageComponent id="appModule.signInFailedServer" />
              </Typography>
            </Box>
          )}

          {props.twoFactorRequired ? (
            <React.Fragment>
              <Box mt={2}>
                <Typography variant="body1">
                  <IntlMessageComponent id="appModule.twofactor.intro" />
                </Typography>
              </Box>

              {props.currentStep === 'qr' ? (
                <TextField
                  id="form.twofactor.qr"
                  label={<IntlMessageComponent id="appModule.twofactor.qrlabel" />}
                  fullWidth
                  margin="normal"
                  onChange={props.handleQrpinChange}
                  onKeyDown={onKeyDownValidate}
                  type="password"
                  value={props.qrpin}
                />
              ) : (
                <TextField
                  id="form.twofactor.sms"
                  label={<IntlMessageComponent id="appModule.twofactor.smslabel" />}
                  fullWidth
                  margin="normal"
                  onChange={props.handleSmscodeChange}
                  onKeyDown={onKeyDownValidate}
                  type="password"
                  value={props.smscode}
                />
              )}

              {loginFailedMessage === 'invalid_2fa' && (
                <Box mt={1}>
                  <Typography variant="body2" color="error">
                    <IntlMessageComponent id="appModule.signInFailed.invalid2fa" />
                  </Typography>
                </Box>
              )}

              <Box mt={3}>
                <ButtonComponent
                  color="primary"
                  variant="contained"
                  onClick={props.onValidateTwoFactor}
                  isProcessing={props.isSendingSms}
                  fullWidth
                >
                  <IntlMessageComponent id="general.validate" />
                </ButtonComponent>
              </Box>

              <Box mt={1}>
                <Link to={RESET_2FA}>
                  <IntlMessageComponent id="twofactor.reset.link" />
                </Link>
              </Box>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <TextField
                id="form.login"
                label={<IntlMessageComponent id="appModule.name" />}
                fullWidth
                margin="normal"
                onChange={props.handleLoginChange}
                onKeyDown={onKeyDown}
                value={props.identifier}
              />

              <TextField
                id="form.password"
                label={<IntlMessageComponent id="appModule.password" />}
                fullWidth
                margin="normal"
                onChange={props.handlePasswordChange}
                onKeyDown={onKeyDown}
                type="password"
                value={props.password}
              />

              <Box mt={3}>
                <ButtonComponent
                  color="primary"
                  variant="contained"
                  onClick={props.onLogin}
                  fullWidth
                >
                  <IntlMessageComponent id="appModule.signIn" />
                </ButtonComponent>
              </Box>

              <Box mt={1}>
                <Link to={LOST_PASSWORD}>
                  <IntlMessageComponent id="page.login.passwordForgotten" />
                </Link>
              </Box>

              <Box mt={3}>
                <PageTitleViewComponent id="page.login.subscribe" />
              </Box>

              <ButtonComponent
                color="secondary"
                variant="contained"
                component={Link}
                to={SIGN_UP}
                fullWidth
              >
                <IntlMessageComponent id="page.login.subscribe.button" />
              </ButtonComponent>
            </React.Fragment>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
